import { PsvProviderStatus } from "@api/shared-runtime/psv-review";
import type { WorkspaceExpirationFilter } from "@api/shared-runtime/workspace-expiration";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { CredentialsCell } from "./workspace-table/credential-cells";
import {
  LastCompletedCell,
  RunVerificationCell,
  VerificationStatusCell,
} from "./workspace-table/verification-cells";
import type { WorkspaceProvider } from "./workspace-types";

type RowActions = {
  onOpen: (provider: WorkspaceProvider) => void;
  onRunVerification: (provider: WorkspaceProvider) => void;
};

type RowOptions = {
  showVerification: boolean;
  compact: boolean;
  expiration?: WorkspaceExpirationFilter;
};

/** Whether the provider page has anything this viewer may see. Pools count:
 * the page header is the only place the roster shows them. */
function canOpenProvider(provider: WorkspaceProvider) {
  return (
    provider.capabilities.canViewCredentials ||
    provider.capabilities.canViewPools ||
    provider.capabilities.canChat ||
    provider.capabilities.canChangeEmployment ||
    provider.capabilities.canViewJobInterests
  );
}

function rowTint(provider: WorkspaceProvider) {
  return provider.psvStatus?.status === PsvProviderStatus.Concern
    ? "bg-red-50/60 hover:bg-red-50"
    : undefined;
}

/** One markup for every width: on narrow screens rows and cells stack, and
 * each cell that needs it names itself. */
const CELL = "block px-4 py-2 md:table-cell md:py-4";

export function WorkspaceTable({
  providers,
  showVerification,
  compact,
  expiration,
  onOpen,
  onRunVerification,
}: RowActions & RowOptions & { providers: WorkspaceProvider[] }) {
  const actions = { onOpen, onRunVerification };
  return (
    <Table className="md:min-w-[980px]">
      <TableHeader className="hidden md:table-header-group">
        <TableRow className="bg-muted/40 text-xs">
          <TableHead>Provider / NPI</TableHead>
          <TableHead>Role / Specialty</TableHead>
          {!compact && <TableHead>Employment</TableHead>}
          <TableHead>Credentials</TableHead>
          {showVerification && (
            <>
              <TableHead>Verification status</TableHead>
              <TableHead>Last completed on</TableHead>
              <TableHead>Run verification</TableHead>
            </>
          )}
        </TableRow>
      </TableHeader>
      <TableBody className="block md:table-row-group">
        {providers.map((provider) => (
          <TableRow
            key={provider.affiliationId}
            className={cn(
              "block py-2 md:table-row md:py-0",
              canOpenProvider(provider) && "cursor-pointer",
              rowTint(provider),
            )}
            onClick={() => {
              if (canOpenProvider(provider)) {
                onOpen(provider);
              }
            }}
          >
            <TableCell className={CELL}>
              <ProviderCell provider={provider} onOpen={onOpen} />
            </TableCell>
            <TableCell className={cn(CELL, "md:max-w-56")}>
              <RoleCell provider={provider} />
            </TableCell>
            {!compact && (
              <TableCell className={CELL}>
                <EmploymentBadge provider={provider} />
              </TableCell>
            )}
            <TableCell className={CELL}>
              <CredentialsCell
                provider={provider}
                expiration={expiration}
                showVerification={showVerification}
              />
            </TableCell>
            {showVerification && (
              <VerificationRowCells provider={provider} {...actions} />
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function VerificationRowCells({
  provider,
  onOpen,
  onRunVerification,
}: { provider: WorkspaceProvider } & RowActions) {
  const psv = provider.psvStatus;
  return (
    <>
      <TableCell className={CELL}>
        {psv && <VerificationStatusCell psv={psv} />}
      </TableCell>
      <TableCell className={CELL}>
        {psv && <LastCompletedCell psv={psv} />}
      </TableCell>
      <TableCell className={CELL}>
        {psv && (
          <RunVerificationCell
            provider={provider}
            psv={psv}
            onOpen={onOpen}
            onRunVerification={onRunVerification}
          />
        )}
      </TableCell>
    </>
  );
}

function ProviderCell({
  provider,
  onOpen,
}: {
  provider: WorkspaceProvider;
  onOpen: (provider: WorkspaceProvider) => void;
}) {
  const canOpen = canOpenProvider(provider);
  return (
    <div>
      <Button
        variant="link"
        className="h-auto whitespace-normal p-0 text-left font-semibold text-foreground"
        disabled={!canOpen}
        title={
          canOpen ? "Open provider details" : "No available provider actions"
        }
        onClick={(event) => {
          event.stopPropagation();
          onOpen(provider);
        }}
      >
        {provider.name || "Name not provided"}
      </Button>
      {provider.npi && (
        <div className="mt-0.5 text-xs text-muted-foreground">
          {provider.npi}
        </div>
      )}
    </div>
  );
}

function RoleCell({ provider }: { provider: WorkspaceProvider }) {
  return (
    <>
      <div className="whitespace-normal text-sm font-medium">
        {provider.specialty || "—"}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">
        {provider.role || "—"}
      </div>
    </>
  );
}

function EmploymentBadge({ provider }: { provider: WorkspaceProvider }) {
  return (
    <Badge variant="outline">
      {provider.employment === "hired" ? "Hired" : "Candidate"}
    </Badge>
  );
}
